import { createReducer, on } from '@ngrx/store';
import { INSAutoCompleteGroup, INSAutoCompleteOption } from '@norfolk-southern/accessns-components';

import {
  IThirdPartyEcrData,
  IThirdPartyEcrEquipmentTypes,
  IThirdPartyLineOfBusiness,
  IThirdPartyMultipleLocations
} from '@ruby/modules/third-party/models/third-party.interface';
import * as ThirdPartyAuthorizationActions from '@ruby/modules/third-party/store/actions/third-party-authorization.actions';
import { IStationDetail } from '@ruby/shared/models/commons/station.interface';
import { IError } from '@ruby/shared/models/request/error.interface';

export const featureKey = 'authorizations';

export interface State {
  loading: boolean;
  loadingEquipments: boolean;
  isLoadingDS: boolean;
  isLoadingStcc: boolean;
  isLoadingScac: boolean;
  lob: Array<IThirdPartyLineOfBusiness>;
  locationsByLOB?: IThirdPartyMultipleLocations;
  equipments: Array<IThirdPartyEcrEquipmentTypes>;
  selectedLOB?: string;
  apTransp: boolean;
  apEcr: boolean;
  apHljs: boolean;
  apRating: boolean;
  apRevenue: boolean;
  apRevenueClaims: boolean;
  apStorageAndLift: boolean;
  authorizationStatus: boolean;
  states: Array<INSAutoCompleteGroup>;
  destinationStations: Array<IStationDetail>;
  stcc: Array<INSAutoCompleteOption>;
  scac: Array<INSAutoCompleteOption>;
  ecrData?: IThirdPartyEcrData;
  error?: IError;
}

export const initialState: State = {
  loading: false,
  loadingEquipments: false,
  isLoadingDS: false,
  isLoadingStcc: false,
  isLoadingScac: false,
  lob: [],
  equipments: [],
  locationsByLOB: undefined,
  selectedLOB: undefined,
  apTransp: false,
  apEcr: false,
  apHljs: false,
  apRating: false,
  apRevenue: false,
  apRevenueClaims: false,
  apStorageAndLift: false,
  authorizationStatus: false,
  states: [],
  destinationStations: [],
  stcc: [],
  scac: [],
  ecrData: undefined,
  error: undefined
};

export const reducer = createReducer(
  initialState,
  on(ThirdPartyAuthorizationActions.loadAvailableLineOfBusiness, (state, action) => ({
    ...state,
    loading: true,
    lob: [],
    locations: [],
    error: undefined
  })),
  on(ThirdPartyAuthorizationActions.loadAvailableLineOfBusinessSuccess, (state, action) => ({
    ...state,
    loading: false,
    lob: action.data
  })),
  on(ThirdPartyAuthorizationActions.loadAvailableLineOfBusinessFailure, (state, action) => ({
    ...state,
    loading: false,
    error: action.error
  })),
  on(ThirdPartyAuthorizationActions.setSelectedLocations, (state, action) => ({
    ...state,
    locationsByLOB: {
      ...state.locationsByLOB,
      [action.data.type]: {
        customer: action.data.customer
      }
    }
  })),
  on(ThirdPartyAuthorizationActions.loadEquipmentTypes, (state, action) => ({
    ...state,
    loadingEquipments: true,
    equipments: [],
    error: undefined
  })),
  on(ThirdPartyAuthorizationActions.loadEquipmentTypesSuccess, (state, action) => ({
    ...state,
    loadingEquipments: false,
    equipments: action.data
  })),
  on(ThirdPartyAuthorizationActions.loadEquipmentTypesFailure, (state, action) => ({
    ...state,
    loadingEquipments: false,
    error: action.error
  })),
  on(ThirdPartyAuthorizationActions.loadStatesSuccess, (state, { data }) => ({
    ...state,
    states: data
  })),
  on(ThirdPartyAuthorizationActions.loadStatesFailure, (state, action) => ({
    ...state,
    states: [],
    error: action.error
  })),
  on(ThirdPartyAuthorizationActions.setDestinationStations, (state, { data }) => ({
    ...state,
    destinationStations: data,
    isLoadingDS: false
  })),
  on(ThirdPartyAuthorizationActions.loadDestinationStations, (state, { data }) => ({
    ...state,
    isLoadingDS: true
  })),
  on(ThirdPartyAuthorizationActions.loadDestinationStationsSuccess, (state, { data }) => ({
    ...state,
    destinationStations: data,
    isLoadingDS: false
  })),
  on(ThirdPartyAuthorizationActions.loadDestinationStationsFailure, (state, action) => ({
    ...state,
    isLoadingDS: false,
    destinationStations: [],
    error: action.error
  })),

  on(ThirdPartyAuthorizationActions.setStcc, (state, { data }) => ({
    ...state,
    stcc: data,
    isLoadingStcc: false,
    error: undefined
  })),
  on(ThirdPartyAuthorizationActions.loadStcc, (state, { data }) => ({
    ...state,
    isLoadingStcc: true,
    stcc: []
  })),
  on(ThirdPartyAuthorizationActions.loadStccSuccess, (state, { data }) => ({
    ...state,
    stcc: data,
    isLoadingStcc: false
  })),
  on(ThirdPartyAuthorizationActions.loadStccFailure, (state, action) => ({
    ...state,
    isLoadingStcc: false,
    stcc: [],
    error: action.error
  })),

  on(ThirdPartyAuthorizationActions.loadScac, (state, action) => ({
    ...state,
    isLoadingScac: true,
    scac: [],
    error: undefined
  })),
  on(ThirdPartyAuthorizationActions.loadScacSuccess, (state, { data }) => ({
    ...state,
    scac: data,
    isLoadingScac: false
  })),
  on(ThirdPartyAuthorizationActions.loadScacFailure, (state, action) => ({
    ...state,
    isLoadingScac: false,
    scac: [],
    error: action.error
  })),
  on(ThirdPartyAuthorizationActions.setEcrData, (state, action) => ({
    ...state,
    ecrData: action.data
  })),
  on(ThirdPartyAuthorizationActions.setSelectedLOB, (state, action) => ({
    ...state,
    selectedLOB: action.data
  })),
  on(ThirdPartyAuthorizationActions.setTransportation, (state, action) => ({
    ...state,
    apTransp: action.data
  })),
  on(ThirdPartyAuthorizationActions.setEcr, (state, action) => ({
    ...state,
    apEcr: action.data
  })),
  on(ThirdPartyAuthorizationActions.setRating, (state, action) => ({
    ...state,
    apRating: action.data
  })),
  on(ThirdPartyAuthorizationActions.setRevenue, (state, action) => ({
    ...state,
    apRevenue: action.data
  })),
  on(ThirdPartyAuthorizationActions.setRevenueClaims, (state, action) => ({
    ...state,
    apRevenueClaims: action.data
  })),
  on(ThirdPartyAuthorizationActions.setStorageAndLift, (state, action) => ({
    ...state,
    apStorageAndLift: action.data
  })),
  on(ThirdPartyAuthorizationActions.setHljs, (state, action) => ({
    ...state,
    apHljs: action.data
  })),
  on(ThirdPartyAuthorizationActions.clear, () => initialState),
  on(ThirdPartyAuthorizationActions.setAuthorizationStatus, (state, action) => ({
    ...state,
    authorizationStatus: action.data
  })),
  on(ThirdPartyAuthorizationActions.setLineOfBusiness, (state, action) => ({
    ...state,
    lob: [action.data]
  })),
  on(ThirdPartyAuthorizationActions.clearApplications, (state, action) => ({
    ...state,
    apHljs: false,
    apTransp: false,
    apEcr: false,
    apRating: false,
    apRevenue: false,
    apStorageAndLift: false,
    ecrData: undefined,
    locationsByLOB: undefined
  }))
);
